// src/components/TrainingPaymentPopup.js
import React, { useState, useEffect } from 'react';
import './TrainingPaymentPopup.css';

// ✅ API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

const TrainingPaymentPopup = ({ 
  isOpen, 
  onClose, 
  candidate, 
  onPaymentSubmit,
  isBulk = false,
  bulkCandidates = [],
  onBulkPaymentSubmit
}) => {
  // ✅ Payment Type
  const [paymentType, setPaymentType] = useState('received');
  
  // ✅ Received from Government
  const [receivedStatus, setReceivedStatus] = useState(null);
  const [receivedMonths, setReceivedMonths] = useState([]);
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // ✅ Paid to Candidate
  const [paidStatus, setPaidStatus] = useState(null);
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);
  const [paidReason, setPaidReason] = useState('');
  
  // ✅ Amount
  const [amount, setAmount] = useState('');
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Month Names
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // ✅ Reset form when popup opens
  useEffect(() => {
    if (isOpen) {
      setPaymentType('received');
      setReceivedStatus(null);
      setReceivedMonths([]);
      setReceivedDate(new Date().toISOString().split('T')[0]);
      setPaidStatus(null);
      setPaidDate(new Date().toISOString().split('T')[0]);
      setPaidReason('');
      setAmount('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // ✅ Toggle month selection
  const toggleMonth = (month) => {
    if (receivedMonths.includes(month)) {
      setReceivedMonths(receivedMonths.filter(m => m !== month));
    } else {
      setReceivedMonths([...receivedMonths, month]);
    }
  };

  // ✅ Get selected months display
  const getSelectedMonthsDisplay = () => {
    if (receivedMonths.length === 0) return 'No months selected';
    return receivedMonths.join(', ');
  };

  // ✅ Convert month name to YYYY-MM format
  const getMonthInYYYYMM = (monthName) => {
    const monthIndex = monthNames.indexOf(monthName) + 1;
    const currentYear = new Date().getFullYear();
    return `${currentYear}-${String(monthIndex).padStart(2, '0')}`;
  };

  // ✅ Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (paymentType === 'received') {
        // ✅ Validate Received from Government
        if (receivedStatus === null) {
          alert('Please select whether payment was received from government or not');
          setIsSubmitting(false);
          return;
        }

        // ✅ Prepare months for received
        let months = [];
        if (receivedStatus === true) {
          if (receivedMonths.length === 0) {
            alert('Please select at least one month');
            setIsSubmitting(false);
            return;
          }
          months = receivedMonths.map(m => getMonthInYYYYMM(m));
        }

        // ✅ Prepare payload for received_from_government
        const payload = {
          application_ids: isBulk ? bulkCandidates.map(c => c.id) : [candidate?.id],
          payment_type: 'received_from_government',
          received: receivedStatus,
          months: months,
          date: receivedStatus ? receivedDate : null,
          amount: amount ? parseFloat(amount) : null,
          remark: null,
          send_whatsapp: true
        };

        console.log('📤 Received Payment Payload:', payload);

        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/applications/bulk-payments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Failed to record payment');
        }

        // ✅ Call callbacks
        if (isBulk && onBulkPaymentSubmit) {
          onBulkPaymentSubmit({
            candidates: bulkCandidates,
            paymentData: { type: 'received', status: receivedStatus ? 'received' : 'not_received', months: receivedMonths, date: receivedDate }
          });
        } else if (!isBulk && onPaymentSubmit) {
          onPaymentSubmit({
            candidate_id: candidate?.id,
            candidate_name: candidate?.candidate_name,
            type: 'received',
            status: receivedStatus ? 'received' : 'not_received',
            months: receivedMonths,
            date: receivedDate,
            amount: amount ? parseFloat(amount) : null
          });
        }

        if (receivedStatus) {
          alert(`✅ Payment received from Government for ${receivedMonths.length} month(s)`);
        } else {
          alert(`❌ Payment not received from Government`);
        }
        onClose();

      } else {
        // ✅ Paid to Candidate
        if (paidStatus === null) {
          alert('Please select whether payment was made to candidate or not');
          setIsSubmitting(false);
          return;
        }

        if (paidStatus === true) {
          if (!paidDate) {
            alert('Please select payment date');
            setIsSubmitting(false);
            return;
          }
        } else {
          if (!paidReason.trim()) {
            alert('Please enter reason for not making payment');
            setIsSubmitting(false);
            return;
          }
        }

        // ✅ For paid_to_candidate, we need to send months
        // If no months selected, use current month
        let months = [];
        if (paidStatus === true) {
          // For paid status, we need at least one month
          // Use current month if no months selected
          months = [getCurrentMonth()];
        }

        // ✅ Prepare payload for paid_to_candidate
        const payload = {
          application_ids: isBulk ? bulkCandidates.map(c => c.id) : [candidate?.id],
          payment_type: 'paid_to_candidate',
          paid: paidStatus,
          months: months, // ✅ Always send months array
          date: paidStatus ? paidDate : null,
          amount: amount ? parseFloat(amount) : null,
          reason: paidStatus ? null : paidReason,
          remark: null,
          send_whatsapp: true
        };

        console.log('📤 Paid Payment Payload:', payload);

        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/applications/bulk-payments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Failed to record payment');
        }

        // ✅ Call callbacks
        if (isBulk && onBulkPaymentSubmit) {
          onBulkPaymentSubmit({
            candidates: bulkCandidates,
            paymentData: { type: 'made', status: paidStatus ? 'paid' : 'not_paid', date: paidDate, reason: paidReason }
          });
        } else if (!isBulk && onPaymentSubmit) {
          onPaymentSubmit({
            candidate_id: candidate?.id,
            candidate_name: candidate?.candidate_name,
            type: 'made',
            status: paidStatus ? 'paid' : 'not_paid',
            date: paidDate,
            reason: paidReason,
            amount: amount ? parseFloat(amount) : null
          });
        }

        if (paidStatus) {
          alert(`✅ Payment made to ${isBulk ? 'candidates' : candidate?.candidate_name} on ${paidDate}`);
        } else {
          alert(`❌ Payment not made to ${isBulk ? 'candidates' : candidate?.candidate_name}\nReason: ${paidReason}`);
        }
        onClose();
      }

    } catch (err) {
      console.error('❌ Payment error:', err);
      alert(`❌ Failed to update payment: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="payment-modal-header">
          <h3>
            <i className="fas fa-money-bill-wave" style={{ color: '#27ae60' }}></i>
            {isBulk ? 'Bulk Payment Details' : 'Payment Details'}
          </h3>
          <button className="payment-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="payment-modal-body">
          {/* ✅ Single Candidate Info */}
          {!isBulk && candidate && (
            <div className="payment-candidate-info">
              <div className="payment-candidate-avatar">
                {candidate?.candidate_name?.split(' ').map(n => n[0]).join('') || 'U'}
              </div>
              <div>
                <div className="payment-candidate-name">{candidate?.candidate_name || 'Unknown'}</div>
                <div className="payment-candidate-email">{candidate?.candidate_email || 'N/A'}</div>
              </div>
            </div>
          )}

          {/* ✅ Bulk Candidates Info - Only Count */}
          {isBulk && bulkCandidates.length > 0 && (
            <div className="payment-candidate-info bulk-info">
              <div className="bulk-candidate-count">
                <i className="fas fa-users"></i>
                <span>{bulkCandidates.length} Candidates Selected</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* ✅ Payment Type Selection */}
            <div className="payment-type-section">
              <label className="payment-type-label">Payment Type</label>
              <div className="payment-type-options">
                <label className={`payment-type-option ${paymentType === 'received' ? 'selected-received' : ''}`}>
                  <input
                    type="radio"
                    name="paymentType"
                    value="received"
                    checked={paymentType === 'received'}
                    onChange={() => setPaymentType('received')}
                  />
                  <span>
                    <i className="fas fa-arrow-down" style={{ color: '#27ae60' }}></i>
                    Received from Government
                  </span>
                </label>
                <label className={`payment-type-option ${paymentType === 'made' ? 'selected-made' : ''}`}>
                  <input
                    type="radio"
                    name="paymentType"
                    value="made"
                    checked={paymentType === 'made'}
                    onChange={() => setPaymentType('made')}
                  />
                  <span>
                    <i className="fas fa-arrow-up" style={{ color: '#e74c3c' }}></i>
                    Paid to Candidate
                  </span>
                </label>
              </div>
            </div>

            {/* ✅ Received from Government Form */}
            {paymentType === 'received' && (
              <div className="payment-form-received">
                <div className="payment-status-section">
                  <label className="payment-status-label">Did you receive payment from Government?</label>
                  <div className="payment-status-options">
                    <label className={`payment-status-option ${receivedStatus === true ? 'selected-yes' : ''}`}>
                      <input
                        type="radio"
                        name="receivedStatus"
                        value="yes"
                        checked={receivedStatus === true}
                        onChange={() => {
                          setReceivedStatus(true);
                          setReceivedMonths([]);
                        }}
                      />
                      <span className="status-yes">
                        <i className="fas fa-check-circle"></i> Yes, Received
                      </span>
                    </label>
                    <label className={`payment-status-option ${receivedStatus === false ? 'selected-no' : ''}`}>
                      <input
                        type="radio"
                        name="receivedStatus"
                        value="no"
                        checked={receivedStatus === false}
                        onChange={() => setReceivedStatus(false)}
                      />
                      <span className="status-no">
                        <i className="fas fa-times-circle"></i> Not Received
                      </span>
                    </label>
                  </div>
                </div>

                {receivedStatus === true && (
                  <>
                    <div className="payment-form-group">
                      <label>Select Months <span className="required-star">*</span></label>
                      <div className="month-grid">
                        {monthNames.map((month) => (
                          <label key={month} className={`month-chip ${receivedMonths.includes(month) ? 'selected' : ''}`}>
                            <input
                              type="checkbox"
                              checked={receivedMonths.includes(month)}
                              onChange={() => toggleMonth(month)}
                            />
                            <span>{month}</span>
                          </label>
                        ))}
                      </div>
                      <div className="selected-months-display">
                        <strong>Selected:</strong> {getSelectedMonthsDisplay()}
                      </div>
                    </div>

                    <div className="payment-form-group">
                      <label>Received Date</label>
                      <input
                        type="date"
                        className="payment-form-input"
                        value={receivedDate}
                        onChange={(e) => setReceivedDate(e.target.value)}
                        required
                      />
                    </div>
                  </>
                )}

                {receivedStatus === false && (
                  <div className="payment-not-received-message">
                    <i className="fas fa-info-circle"></i>
                    <span>Payment not received from Government</span>
                  </div>
                )}
              </div>
            )}

            {/* ✅ Paid to Candidate Form */}
            {paymentType === 'made' && (
              <div className="payment-form-made">
                <div className="payment-status-section">
                  <label className="payment-status-label">Did you pay to candidate?</label>
                  <div className="payment-status-options">
                    <label className={`payment-status-option ${paidStatus === true ? 'selected-yes' : ''}`}>
                      <input
                        type="radio"
                        name="paidStatus"
                        value="yes"
                        checked={paidStatus === true}
                        onChange={() => {
                          setPaidStatus(true);
                          setPaidDate(new Date().toISOString().split('T')[0]);
                          setPaidReason('');
                        }}
                      />
                      <span className="status-yes">
                        <i className="fas fa-check-circle"></i> Yes, Paid
                      </span>
                    </label>
                    <label className={`payment-status-option ${paidStatus === false ? 'selected-no' : ''}`}>
                      <input
                        type="radio"
                        name="paidStatus"
                        value="no"
                        checked={paidStatus === false}
                        onChange={() => setPaidStatus(false)}
                      />
                      <span className="status-no">
                        <i className="fas fa-times-circle"></i> Not Paid
                      </span>
                    </label>
                  </div>
                </div>

                {paidStatus === true && (
                  <div className="payment-form-group">
                    <label>Payment Date</label>
                    <input
                      type="date"
                      className="payment-form-input"
                      value={paidDate}
                      onChange={(e) => setPaidDate(e.target.value)}
                      required
                    />
                  </div>
                )}

                {paidStatus === false && (
                  <div className="payment-form-group">
                    <label>Reason for Not Paying <span className="required-star">*</span></label>
                    <textarea
                      className="payment-form-textarea"
                      placeholder="Please explain why payment was not made..."
                      value={paidReason}
                      onChange={(e) => setPaidReason(e.target.value)}
                      rows="3"
                      required
                    />
                  </div>
                )}
              </div>
            )}

            {/* ✅ Amount (Optional) */}
            {/* <div className="payment-form-group">
              <label>Amount (Optional)</label>
              <input
                type="number"
                className="payment-form-input"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0"
                step="100"
              />
            </div> */}

            <div className="payment-modal-actions">
              <button 
                type="button" 
                className="payment-btn-cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="payment-btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i> {isBulk ? `Update ${bulkCandidates.length} Candidates` : 'Record Payment'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TrainingPaymentPopup;